/**
 * Profile page
 *
 * US-023 PR-B: extends PR #1's profile wiring with athlete profile sections
 * (athletic info, personal brand tags, per-platform social, deal preferences).
 * Single form, single submit — all fields flow through `api.user.update`
 * via the extended `updateProfileSchema`.
 */

import { track } from '@app/analytics/client';
import { getPlanDisplayName } from '@app/features/billing';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  TagInput,
  Textarea,
  toast,
} from '@app/ui';
import { PageTransition, StaggerContainer, StaggerItem } from '@app/ui/motion';
import { type UpdateProfileInput, updateProfileSchema } from '@app/validators';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { EligibilityCard } from '../../components/eligibility-card';
import { RouteErrorComponent } from '../../components/route-error';
import { api, client } from '../../lib/api';

// ---------------------------------------------------------------------------
// Option lists for US-023 selects / multi-selects
// ---------------------------------------------------------------------------

const CLASS_YEARS = [
  { value: 'freshman', label: 'Freshman' },
  { value: 'sophomore', label: 'Sophomore' },
  { value: 'junior', label: 'Junior' },
  { value: 'senior', label: 'Senior' },
  { value: 'graduate', label: 'Graduate' },
  { value: 'redshirt', label: 'Redshirt' },
] as const;

const DIVISIONS = [
  { value: 'd1', label: 'D1' },
  { value: 'd2', label: 'D2' },
  { value: 'd3', label: 'D3' },
  { value: 'naia', label: 'NAIA' },
  { value: 'juco', label: 'JUCO' },
] as const;

const DEAL_TYPES = [
  { value: 'endorsement', label: 'Endorsement' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'appearance', label: 'Appearance' },
  { value: 'product', label: 'Product / Gifting' },
  { value: 'event', label: 'Event' },
  { value: 'content_creation', label: 'Content Creation' },
  { value: 'camp_clinic', label: 'Camp / Clinic' },
  { value: 'autograph', label: 'Autograph / Memorabilia' },
] as const;

const INDUSTRIES = [
  { value: 'apparel', label: 'Apparel' },
  { value: 'footwear', label: 'Footwear' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'tech', label: 'Tech' },
  { value: 'finance', label: 'Finance' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'beauty', label: 'Beauty & Personal Care' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'health_wellness', label: 'Health & Wellness' },
  { value: 'nutrition', label: 'Nutrition' },
  { value: 'telecom', label: 'Telecom' },
  { value: 'retail', label: 'Retail' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'education', label: 'Education' },
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'other', label: 'Other' },
] as const;

// ---------------------------------------------------------------------------
// Small inline helper for toggle-pill multi-selects
// ---------------------------------------------------------------------------

function MultiSelectPills({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: string[];
  onChange: (values: string[]) => void;
  options: readonly { value: string; label: string }[];
  ariaLabel: string;
}) {
  const toggle = (v: string) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  return (
    <div role="group" aria-label={ariaLabel} className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => toggle(o.value)}
            className={
              on
                ? 'rounded-full border border-primary bg-primary px-3 py-1 text-xs font-medium text-primary-foreground shadow-sm'
                : 'rounded-full border border-input bg-transparent px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-secondary-foreground'
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute('/_authenticated/profile')({
  validateSearch: (search: Record<string, unknown>) => ({
    upgraded: search.upgraded === 'true' || search.upgraded === true,
  }),
  loader: async ({ context }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    await Promise.all([
      context.queryClient.ensureQueryData(serverApi.user.me.queryOptions()),
      context.queryClient.ensureQueryData(serverApi.subscription.current.queryOptions()),
    ]);
  },
  pendingComponent: () => (
    <div className="max-w-3xl animate-pulse">
      <div className="h-8 bg-muted rounded w-48 mb-6" />
      <div className="h-64 bg-muted rounded" />
    </div>
  ),
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: Profile,
});

function Profile() {
  const { data: profile } = useSuspenseQuery(api.user.me.queryOptions());
  const { data: subscription } = useSuspenseQuery(api.subscription.current.queryOptions());
  const queryClient = useQueryClient();
  const { upgraded } = Route.useSearch();

  // Fire checkout_returned exactly once, on first mount with ?upgraded=true.
  // Kept in its own effect (with empty-ish deps) so polling-driven status
  // changes can't re-trigger it and inflate funnel conversion rates.
  // We intentionally snapshot plan/status at first render via useRef-less
  // closure capture — the polling effect below handles the data refresh.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire-once is the contract
  useEffect(() => {
    if (!upgraded) return;
    track('checkout_returned', {
      plan_at_return: subscription.plan,
      status_at_return: subscription.status,
    });
  }, [upgraded]);

  // When the user returns from Stripe Checkout (?upgraded=true) the subscription
  // row may still be 'incomplete' for a few seconds — Stripe needs to deliver
  // checkout.session.completed and our webhook needs to backfill the row.
  // Poll until the row flips to active/trialing (or 30s elapses).
  useEffect(() => {
    if (!upgraded) return;
    if (subscription.status !== 'incomplete' && subscription.status !== 'none') return;
    const key = api.subscription.current.queryKey();
    const start = Date.now();
    const interval = setInterval(() => {
      if (Date.now() - start > 30_000) {
        clearInterval(interval);
        return;
      }
      queryClient.invalidateQueries({ queryKey: key });
    }, 2_000);
    return () => clearInterval(interval);
  }, [upgraded, subscription.status, queryClient]);

  const form = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      // Legacy
      name: profile.name || '',
      sport: profile.sport || '',
      school: profile.school || '',
      socialFollowers: profile.socialFollowers ? String(profile.socialFollowers) : '',
      socialEngagement: profile.socialEngagement ? String(profile.socialEngagement) : '',
      phoneNumber: profile.phoneNumber || '',
      language: (profile.language as 'en' | 'es') || 'en',

      // US-023: athletic info
      position: profile.position ?? null,
      classYear: profile.classYear ?? null,
      conference: profile.conference ?? null,
      division: profile.division ?? null,
      hometown: profile.hometown ?? null,

      // US-023: personal brand
      bio: profile.bio ?? null,
      personalValues: profile.personalValues ?? [],
      interests: profile.interests ?? [],
      causes: profile.causes ?? [],

      // US-023: per-platform social
      instagramHandle: profile.instagramHandle ?? null,
      instagramFollowers: profile.instagramFollowers ?? null,
      tiktokHandle: profile.tiktokHandle ?? null,
      tiktokFollowers: profile.tiktokFollowers ?? null,
      twitterHandle: profile.twitterHandle ?? null,
      twitterFollowers: profile.twitterFollowers ?? null,
      youtubeHandle: profile.youtubeHandle ?? null,
      youtubeFollowers: profile.youtubeFollowers ?? null,

      // US-023: deal preferences
      preferredDealTypes: profile.preferredDealTypes ?? [],
      preferredIndustries: profile.preferredIndustries ?? [],
      minimumDealSize: profile.minimumDealSize ?? null,
      openToDeals: profile.openToDeals ?? true,
    },
  });

  const updateMutation = useMutation({
    ...api.user.update.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: api.user.me.queryKey() });
      queryClient.invalidateQueries({ queryKey: api.match.forBrands.queryKey() });
      queryClient.invalidateQueries({ queryKey: api.match.forBrand.queryKey() });
      toast.success('Profile saved');
    },
    onError: (e) => {
      toast.error(`Failed to save: ${e.message}`);
    },
  });

  const onSubmit = (data: UpdateProfileInput) => {
    updateMutation.mutate({
      // Legacy
      name: data.name,
      sport: data.sport || undefined,
      school: data.school || undefined,
      socialFollowers: data.socialFollowers ? Number(data.socialFollowers) : undefined,
      socialEngagement: data.socialEngagement ? Number(data.socialEngagement) : undefined,
      phoneNumber: data.phoneNumber || undefined,
      language: data.language,

      // US-023: athletic info
      position: data.position,
      classYear: data.classYear,
      conference: data.conference,
      division: data.division,
      hometown: data.hometown,

      // US-023: personal brand
      bio: data.bio,
      personalValues: data.personalValues,
      interests: data.interests,
      causes: data.causes,

      // US-023: per-platform social
      instagramHandle: data.instagramHandle,
      instagramFollowers: data.instagramFollowers,
      tiktokHandle: data.tiktokHandle,
      tiktokFollowers: data.tiktokFollowers,
      twitterHandle: data.twitterHandle,
      twitterFollowers: data.twitterFollowers,
      youtubeHandle: data.youtubeHandle,
      youtubeFollowers: data.youtubeFollowers,

      // US-023: deal preferences
      preferredDealTypes: data.preferredDealTypes,
      preferredIndustries: data.preferredIndustries,
      minimumDealSize: data.minimumDealSize,
      openToDeals: data.openToDeals,
    });
  };

  const handleUpgrade = async () => {
    track('upgrade_cta_clicked', {
      source: 'profile_card',
      current_plan: subscription.plan,
      target_plan: 'pro',
    });
    try {
      const { subscription: sub } = await import('@app/auth/client');
      // See useUpgrade — prefer VITE_APP_URL over window.location.origin so
      // checkouts started from a *.vercel.app alias still land on the
      // canonical host where the Stripe webhook is registered.
      const canonicalOrigin = import.meta.env.VITE_APP_URL || window.location.origin;
      const result = await sub.upgrade({
        plan: 'pro',
        successUrl: `${canonicalOrigin}/profile?upgraded=true`,
        cancelUrl: `${canonicalOrigin}/profile`,
      });
      if (result.error) {
        toast.error(`Failed to create checkout: ${result.error.message ?? 'Unknown error'}`);
        return;
      }
      if (result.data?.url) {
        window.location.href = result.data.url;
        return;
      }
      // No URL and no error — usually a misconfigured price (missing
      // STRIPE_PRICE_*) or a plan not enabled in the auth config. Surface
      // it rather than silently dropping the click.
      toast.error("Couldn't start checkout. Try again, or contact support if it persists.");
    } catch (e) {
      toast.error(`Failed to create checkout: ${e instanceof Error ? e.message : 'Unknown error'}`);
    }
  };

  const handleManageBilling = async () => {
    track('billing_portal_opened', {
      from: subscription.status === 'past_due' ? 'payment_failed_banner' : 'profile_card',
      current_plan: subscription.plan,
    });
    try {
      const { subscription: sub } = await import('@app/auth/client');
      const result = await sub.billingPortal({
        returnUrl: `${window.location.origin}/profile`,
      });
      if (result.error) {
        toast.error(`Failed to open billing portal: ${result.error.message ?? 'Unknown error'}`);
        return;
      }
      if (result.data?.url) {
        window.location.href = result.data.url;
        return;
      }
      toast.error(
        "Couldn't open the billing portal. Try again, or contact support if it persists.",
      );
    } catch (e) {
      toast.error(
        `Failed to open billing portal: ${e instanceof Error ? e.message : 'Unknown error'}`,
      );
    }
  };

  return (
    <PageTransition className="max-w-3xl">
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">Profile & Settings</h1>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <StaggerContainer className="space-y-6">
            {/* ---- Account (legacy fields) ---- */}
            <StaggerItem>
              <Card>
                <CardHeader>
                  <CardTitle>Athlete Profile</CardTitle>
                  <CardDescription>
                    Your profile helps us provide accurate deal valuations
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Your name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <Input type="email" disabled value={profile.email} />
                      </FormItem>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="sport"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Sport</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g., Football, Basketball" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="school"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>School</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g., University of Texas" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="socialFollowers"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Social Followers (aggregate)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="Total across platforms"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="socialEngagement"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Engagement Rate (%)</FormLabel>
                            <FormControl>
                              <Input type="number" step="0.1" placeholder="e.g., 3.5" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <Input type="tel" placeholder="+1 (555) 000-0000" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="language"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Language</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="en">English</SelectItem>
                              <SelectItem value="es">Spanish</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* ---- US-031: NCAA Eligibility ---- */}
            <StaggerItem>
              <EligibilityCard user={profile} />
            </StaggerItem>

            {/* ---- US-023: Athletic Info ---- */}
            <StaggerItem>
              <Card>
                <CardHeader>
                  <CardTitle>Athletic Info</CardTitle>
                  <CardDescription>
                    Details about your college athletic career. Helps brands find you.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="position"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Position</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g., Point Guard, Wide Receiver"
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value || null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="classYear"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Class Year</FormLabel>
                          <Select
                            onValueChange={(v) => field.onChange(v || null)}
                            value={field.value ?? ''}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {CLASS_YEARS.map((c) => (
                                <SelectItem key={c.value} value={c.value}>
                                  {c.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="conference"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Conference</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g., Big Ten, SEC"
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value || null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="division"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Division</FormLabel>
                          <Select
                            onValueChange={(v) => field.onChange(v || null)}
                            value={field.value ?? ''}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {DIVISIONS.map((d) => (
                                <SelectItem key={d.value} value={d.value}>
                                  {d.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="hometown"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Hometown</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="City, State"
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value || null)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </StaggerItem>

            {/* ---- US-023: Personal Brand ---- */}
            <StaggerItem>
              <Card>
                <CardHeader>
                  <CardTitle>Personal Brand</CardTitle>
                  <CardDescription>
                    What do you stand for? Brands use values and interests to find athletes whose
                    stories align with their mission.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="bio"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Short Bio</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="A couple sentences about you, your story, what drives you..."
                            maxLength={500}
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value || null)}
                          />
                        </FormControl>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {(field.value ?? '').length}/500
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Controller
                    control={form.control}
                    name="personalValues"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Personal Values</FormLabel>
                        <TagInput
                          value={field.value ?? []}
                          onChange={field.onChange}
                          placeholder="e.g., Family, Faith, Hard Work, Community"
                          maxTags={10}
                          ariaLabel="Personal values"
                        />
                      </FormItem>
                    )}
                  />
                  <Controller
                    control={form.control}
                    name="interests"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Interests</FormLabel>
                        <TagInput
                          value={field.value ?? []}
                          onChange={field.onChange}
                          placeholder="e.g., Fashion, Gaming, Cooking, Travel"
                          maxTags={15}
                          ariaLabel="Interests"
                        />
                      </FormItem>
                    )}
                  />
                  <Controller
                    control={form.control}
                    name="causes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Causes You Care About</FormLabel>
                        <TagInput
                          value={field.value ?? []}
                          onChange={field.onChange}
                          placeholder="e.g., Mental Health, Education Access, Environment"
                          maxTags={10}
                          ariaLabel="Causes"
                        />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </StaggerItem>

            {/* ---- US-023: Social Accounts ---- */}
            <StaggerItem>
              <Card>
                <CardHeader>
                  <CardTitle>Social Accounts</CardTitle>
                  <CardDescription>
                    Per-platform handles and follower counts. Fill in the platforms you actively
                    use.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <SocialRow
                    label="Instagram"
                    form={form}
                    handleKey="instagramHandle"
                    followersKey="instagramFollowers"
                  />
                  <SocialRow
                    label="TikTok"
                    form={form}
                    handleKey="tiktokHandle"
                    followersKey="tiktokFollowers"
                  />
                  <SocialRow
                    label="Twitter / X"
                    form={form}
                    handleKey="twitterHandle"
                    followersKey="twitterFollowers"
                  />
                  <SocialRow
                    label="YouTube"
                    form={form}
                    handleKey="youtubeHandle"
                    followersKey="youtubeFollowers"
                  />
                </CardContent>
              </Card>
            </StaggerItem>

            {/* ---- US-023: Deal Preferences ---- */}
            <StaggerItem>
              <Card>
                <CardHeader>
                  <CardTitle>Deal Preferences</CardTitle>
                  <CardDescription>
                    What kinds of deals are you open to? This filters brand suggestions.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <Controller
                    control={form.control}
                    name="preferredDealTypes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Deal Types</FormLabel>
                        <MultiSelectPills
                          value={field.value ?? []}
                          onChange={field.onChange}
                          options={DEAL_TYPES}
                          ariaLabel="Preferred deal types"
                        />
                      </FormItem>
                    )}
                  />
                  <Controller
                    control={form.control}
                    name="preferredIndustries"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Industries</FormLabel>
                        <MultiSelectPills
                          value={field.value ?? []}
                          onChange={field.onChange}
                          options={INDUSTRIES}
                          ariaLabel="Preferred industries"
                        />
                      </FormItem>
                    )}
                  />
                  <div className="grid sm:grid-cols-2 gap-4 items-end">
                    <FormField
                      control={form.control}
                      name="minimumDealSize"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Minimum Deal Size ($)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={0}
                              step={100}
                              placeholder="e.g., 500"
                              value={field.value ?? ''}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value === '' ? null : Number(e.target.value),
                                )
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Controller
                      control={form.control}
                      name="openToDeals"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center gap-3 pb-2">
                            <Switch
                              checked={field.value ?? true}
                              onCheckedChange={field.onChange}
                            />
                            <FormLabel className="m-0">Open to deals</FormLabel>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            When off, your profile won&apos;t be surfaced to brands as a match.
                          </p>
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* ---- Save button ---- */}
            <StaggerItem>
              <div className="flex items-center gap-3">
                <Button type="submit" disabled={updateMutation.isPending} className="min-h-[44px]">
                  {updateMutation.isPending ? 'Saving…' : 'Save Profile'}
                </Button>
              </div>
            </StaggerItem>

            {/* ---- Notifications (unchanged from PR #1) ---- */}
            <StaggerItem>
              <Card>
                <CardHeader>
                  <CardTitle>Notification Preferences</CardTitle>
                  <CardDescription>
                    Choose how you want to be notified (coming soon)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      {
                        label: 'Payment due reminders',
                        desc: 'Get notified 3 days before and on due date',
                      },
                      { label: 'Deal deadline alerts', desc: 'Deliverable deadline approaching' },
                      { label: 'Overdue payment alerts', desc: 'When a payment becomes overdue' },
                      { label: 'Weekly digest', desc: 'Summary of your deals and earnings' },
                    ].map((pref) => (
                      <div
                        key={pref.label}
                        className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3 py-3 border-b last:border-b-0"
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox defaultChecked disabled className="mt-1" />
                          <div>
                            <p className="text-sm font-medium">{pref.label}</p>
                            <p className="text-xs text-muted-foreground">{pref.desc}</p>
                          </div>
                        </div>
                        <div className="flex gap-3 pl-7 sm:pl-0 sm:ml-auto sm:shrink-0">
                          {['Email', 'SMS', 'In-App'].map((ch) => (
                            <label
                              key={ch}
                              htmlFor={`pref-${pref.label}-${ch}`}
                              className="flex items-center gap-1 text-xs min-h-[44px] sm:min-h-0"
                            >
                              <Checkbox defaultChecked disabled id={`pref-${pref.label}-${ch}`} />
                              {ch}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CTIA SMS Compliance Disclosure */}
                  <div className="mt-4 p-3 rounded-md bg-muted/50 text-xs text-muted-foreground space-y-1">
                    <p>
                      <strong>NilBoard SMS Notifications:</strong> By enabling SMS notifications,
                      you agree to receive text messages about your deal activity, payment
                      reminders, and deadline alerts from NilBoard.
                    </p>
                    <p>Message and data rates may apply.</p>
                    <p>Message frequency varies based on your deal activity.</p>
                    <p>Reply HELP for help. Reply STOP to opt out.</p>
                    <p>
                      <Link to="/terms" className="underline">
                        Terms of Service
                      </Link>{' '}
                      |{' '}
                      <Link to="/privacy" className="underline">
                        Privacy Policy
                      </Link>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* ---- Subscription ---- */}
            <StaggerItem>
              <Card>
                <CardHeader>
                  <CardTitle>Subscription</CardTitle>
                  <CardDescription>Manage your plan</CardDescription>
                </CardHeader>
                <CardContent>
                  <SubscriptionCardBody
                    subscription={subscription}
                    onUpgrade={handleUpgrade}
                    onManageBilling={handleManageBilling}
                    onRefresh={() =>
                      queryClient.invalidateQueries({
                        queryKey: api.subscription.current.queryKey(),
                      })
                    }
                  />
                </CardContent>
              </Card>
            </StaggerItem>
          </StaggerContainer>
        </form>
      </Form>
    </PageTransition>
  );
}

// ---------------------------------------------------------------------------
// SocialRow — one row per platform, binds to two form fields
// ---------------------------------------------------------------------------

type SocialHandleKey = 'instagramHandle' | 'tiktokHandle' | 'twitterHandle' | 'youtubeHandle';
type SocialFollowersKey =
  | 'instagramFollowers'
  | 'tiktokFollowers'
  | 'twitterFollowers'
  | 'youtubeFollowers';

function SocialRow({
  label,
  form,
  handleKey,
  followersKey,
}: {
  label: string;
  form: ReturnType<typeof useForm<UpdateProfileInput>>;
  handleKey: SocialHandleKey;
  followersKey: SocialFollowersKey;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_2fr_1fr] sm:items-end">
      <div className="text-sm font-medium pt-2">{label}</div>
      <FormField
        control={form.control}
        name={handleKey}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs text-muted-foreground">Handle (without @)</FormLabel>
            <FormControl>
              <Input
                placeholder="yourhandle"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value.replace(/^@/, '') || null)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={followersKey}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs text-muted-foreground">Followers</FormLabel>
            <FormControl>
              <Input
                type="number"
                min={0}
                placeholder="0"
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(e.target.value === '' ? null : Number(e.target.value))
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SubscriptionCardBody — renders Free / Pending / Active states
// ---------------------------------------------------------------------------

interface SubscriptionShape {
  plan: string;
  status: string;
  periodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

function SubscriptionCardBody({
  subscription,
  onUpgrade,
  onManageBilling,
  onRefresh,
}: {
  subscription: SubscriptionShape;
  onUpgrade: () => void;
  onManageBilling: () => void;
  onRefresh: () => void;
}) {
  // 'incomplete' = Stripe Checkout finished, webhook hasn't backfilled yet
  //                 → "Activating" (transient, usually a few seconds)
  // 'past_due'   = Stripe attempted a renewal/invoice and the charge failed
  //                 → "Payment Failed" (user action required — open billing portal)
  // These look superficially similar but mean opposite things; never collapse them.
  const isActivating = subscription.status === 'incomplete';
  const isPastDue = subscription.status === 'past_due';
  const isFree = subscription.status === 'none';
  const isPaid = !isActivating && !isPastDue && !isFree;

  if (isActivating) {
    return (
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-medium flex items-center gap-2">
            {getPlanDisplayName(subscription.plan)} Plan
            <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200">
              Activating
            </span>
          </p>
          <p className="text-sm text-muted-foreground">
            Payment received — finalizing your subscription. This usually takes a few seconds.
          </p>
        </div>
        <Button type="button" variant="outline" className="min-h-[44px]" onClick={onRefresh}>
          Refresh
        </Button>
      </div>
    );
  }

  if (isPastDue) {
    return (
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-medium flex items-center gap-2">
            {getPlanDisplayName(subscription.plan)} Plan
            <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200">
              Payment Failed
            </span>
          </p>
          <p className="text-sm text-muted-foreground">
            We couldn't charge your card. Update your payment method to keep your subscription
            active.
          </p>
        </div>
        <Button type="button" className="min-h-[44px]" onClick={onManageBilling}>
          Update Payment Method
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="font-medium">
          {isFree ? 'Free Plan · Up to 5 deals' : `${getPlanDisplayName(subscription.plan)} Plan`}
        </p>
        <p className="text-sm text-muted-foreground">
          {isFree
            ? 'Upgrade for unlimited deals and full AI features.'
            : subscription.cancelAtPeriodEnd
              ? `Cancels ${subscription.periodEnd ? new Date(subscription.periodEnd).toLocaleDateString() : 'soon'}`
              : subscription.periodEnd
                ? `Renews ${new Date(subscription.periodEnd).toLocaleDateString()}`
                : `Status: ${subscription.status}`}
        </p>
      </div>
      {isFree ? (
        <div className="flex flex-col items-stretch sm:items-end gap-1">
          <Button type="button" variant="outline" className="min-h-[44px]" onClick={onUpgrade}>
            Upgrade to Pro — $10/mo
          </Button>
          <Link
            to="/pricing"
            className="text-xs text-muted-foreground hover:underline text-center sm:text-right"
          >
            See all plans
          </Link>
        </div>
      ) : (
        isPaid && (
          <Button
            type="button"
            variant="outline"
            className="min-h-[44px]"
            onClick={onManageBilling}
          >
            Manage Billing
          </Button>
        )
      )}
    </div>
  );
}
